import { call, fork, put, takeLatest, takeEvery } from 'redux-saga/effects'
import {
  CREATE_QUESTIONS,
  UPDATE_QUESTION,
  DELETE_QUESTION,
  DELETE_QUESTIONS,
  FETCH_COLLECTION,
  GET_COMMENTS,
  POST_COMMENT,
} from 'src/actions/actionTypes'
import { popinApiV2 } from 'src/api/api'
import {
  createQuestionsDone,
  createQuestionsError,
  createQuestionsStart,
  createQuestionsSuccess,
  updateQuestionDone,
  updateQuestionError,
  updateQuestionStart,
  updateQuestionSuccess,
  deleteQuestionDone,
  deleteQuestionError,
  deleteQuestionStart,
  deleteQuestionSuccess,
  deleteQuestionsDone,
  deleteQuestionsError,
  deleteQuestionsStart,
  deleteQuestionsSuccess,
  fetchCollectionDone,
  fetchCollectionError,
  fetchCollectionStart,
  fetchCollectionSuccess,
  getCurrentCollection,
  setCurrentFactors,
  setTemplateFactors,
  createPresetQuestionsSuccess,
  createPresetQuestionsError,
  createPresetQuestionsDone,
  getCommentsStart,
  getCommentsSuccess,
  getCommentsError,
  getCommentsDone,
  postCommentStart,
  postCommentSuccess,
  postCommentError,
  postCommentDone,
} from 'src/actions/prioritize'

const getURLbyId = (id) => `/collections/${id}/details`

export function* createQuestionsSaga({ payload }) {
  const { parentId, questions, isTemplate, isPreset } = payload
  try {
    yield put(createQuestionsStart())

    const postQuestionURL = `/collections/${parentId}/questions`
    const collectionURL = getURLbyId(parentId)

    yield call(popinApiV2.post, postQuestionURL, questions)
    const collection = yield call(popinApiV2.get, collectionURL)

    const isOnlyCurrentFactors = !isTemplate && !isPreset
    if (isOnlyCurrentFactors) {
      yield put(getCurrentCollection(collection))
    }
    if (isTemplate) {
      yield put(setTemplateFactors(collection.questions))
    } else {
      yield put(setCurrentFactors(collection.questions))
    }

    yield put(createQuestionsSuccess(collection))
    if (isPreset) yield put(createPresetQuestionsSuccess())
  } catch (error) {
    console.log('createQuestionsSaga error', error)
    yield put(createQuestionsError(error))
    if (isPreset) yield put(createPresetQuestionsError())
  } finally {
    yield put(createQuestionsDone())
    if (isPreset) yield put(createPresetQuestionsDone())
  }
}

export function* updateQuestionSaga({ payload }) {
  try {
    yield put(updateQuestionStart())
    const { parentId, question, isTemplate } = payload

    const putQuestionURL = `/questions/${question.id}`
    const collectionURL = getURLbyId(parentId)

    yield call(popinApiV2.put, putQuestionURL, question)
    const collection = yield call(popinApiV2.get, collectionURL)

    if (isTemplate) {
      yield put(setTemplateFactors(collection.questions))
    } else {
      yield put(getCurrentCollection(collection))
      yield put(setCurrentFactors(collection.questions))
    }

    yield put(updateQuestionSuccess(collection))
  } catch (error) {
    console.log('updateQuestionSaga error', error)
    yield put(updateQuestionError(error))
  } finally {
    yield put(updateQuestionDone())
  }
}

export function* deleteQuestionSaga({ payload }) {
  try {
    yield put(deleteQuestionStart())
    const { parentId, deleteQuestionId, isTemplate } = payload

    const deleteQuestionURL = `/questions/${deleteQuestionId}`
    const collectionURL = getURLbyId(parentId)

    yield call(popinApiV2.del, deleteQuestionURL)
    const collection = yield call(popinApiV2.get, collectionURL)

    if (isTemplate) {
      yield put(setTemplateFactors(collection.questions))
    } else {
      yield put(getCurrentCollection(collection))
      yield put(setCurrentFactors(collection.questions))
    }

    yield put(deleteQuestionSuccess(collection))
  } catch (error) {
    console.log('deleteQuestionSaga error', error)
    yield put(deleteQuestionError(error))
  } finally {
    yield put(deleteQuestionDone())
  }
}

export function* deleteQuestionsSaga({ payload }) {
  try {
    yield put(deleteQuestionsStart())
    const { parentId, isTemplate } = payload

    const URL = `/collections/${parentId}/questions`
    const collectionURL = getURLbyId(parentId)

    yield call(popinApiV2.del, URL)
    const collection = yield call(popinApiV2.get, collectionURL)

    if (isTemplate) {
      yield put(setTemplateFactors(collection.questions))
    } else {
      yield put(getCurrentCollection(collection))
      yield put(setCurrentFactors(collection.questions))
    }

    yield put(deleteQuestionsSuccess(collection))
  } catch (error) {
    console.log('deleteQuestionsSaga error', error)
    yield put(deleteQuestionsError(error))
  } finally {
    yield put(deleteQuestionsDone())
  }
}

export function* fetchCollectionSaga({ payload: id }) {
  try {
    yield put(fetchCollectionStart())
    const collectionURL = getURLbyId(id)
    const collection = yield call(popinApiV2.get, collectionURL)
    yield put(fetchCollectionSuccess(collection))
  } catch (error) {
    console.log('fetchCollectionSaga error', error)
    yield put(fetchCollectionError(error))
  } finally {
    yield put(fetchCollectionDone())
  }
}

export function* getCommentsSaga({ payload: id }) {
  try {
    yield put(getCommentsStart())
    const commentsUrl = `/collections/${id}/comments`
    const comments = yield call(popinApiV2.get, commentsUrl)
    yield put(getCommentsSuccess({ id, comments }))
  } catch (error) {
    console.log('getCommentsSaga error', error)
    yield put(getCommentsError(error))
  } finally {
    yield put(getCommentsDone())
  }
}

export function* postCommentSaga({ payload: body }) {
  try {
    yield put(postCommentStart())
    const { collectionId, payload } = body
    const commentsUrl = `/collections/${collectionId}/comments`
    const comment = yield call(popinApiV2.post, commentsUrl, payload)
    yield put(postCommentSuccess({ id: collectionId, comment }))
  } catch (error) {
    console.log('postCommentSaga error', error)
    yield put(postCommentError(error))
  } finally {
    yield put(postCommentDone())
  }
}

export function* watchCreateQuestionsSaga() {
  yield takeLatest(CREATE_QUESTIONS, createQuestionsSaga)
}

export function* watchUpdateQuestionSaga() {
  yield takeLatest(UPDATE_QUESTION, updateQuestionSaga)
}

export function* watchDeleteQuestionSaga() {
  yield takeLatest(DELETE_QUESTION, deleteQuestionSaga)
}

export function* watchDeleteQuestionsSaga() {
  yield takeLatest(DELETE_QUESTIONS, deleteQuestionsSaga)
}

export function* watchFetchCollectionSaga() {
  yield takeLatest(FETCH_COLLECTION, fetchCollectionSaga)
}

export function* watchGetCommentsSaga() {
  yield takeEvery(GET_COMMENTS, getCommentsSaga)
}

export function* watchPostCommentSaga() {
  yield takeLatest(POST_COMMENT, postCommentSaga)
}

export default [
  fork(watchCreateQuestionsSaga),
  fork(watchUpdateQuestionSaga),
  fork(watchDeleteQuestionSaga),
  fork(watchDeleteQuestionsSaga),
  fork(watchFetchCollectionSaga),
  fork(watchGetCommentsSaga),
  fork(watchPostCommentSaga),
]
